import React from 'react'
import { GuildScore } from '@/atoms/GuildScore'
import { AsH3, EyebrowMD, HeadingMD } from '@/atoms/Text'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge'
import {
  formatTitleRating,
  formatTitleMoviePosterPath,
  formatTitleCrewMap,
  CatalogTitle,
  CatalogMovie,
  CatalogSeries,
  formatTitleReleaseYear,
  formatTitleFriendlyReleaseDate,
  formatTitleDuration,
  isSeries,
  formatTitleLikeVerticalImagePath,
  formatNonTitleLikeLandscapeImagePath,
} from '@/services/ContentCatalog'
import { FilmDetails as FilmDetailsType } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { CastRail } from '@/views/WatchTheatricalView/FilmDetails/CastRail'
import { DescriptorSection } from '@/views/WatchTheatricalView/FilmDetails/DescriptorSection'
import { CrewCredits } from './CrewCredits'

interface FilmDetailsComponentProps {
  catalogTitle: CatalogMovie | CatalogSeries
  filmDetails: FilmDetailsType
  slug: string
  projectType: string
  guildScore?: number
}
export function FilmDetails(props: FilmDetailsComponentProps) {
  const { t } = useTranslate('theatrical-presales')
  const { locale } = useLocale()

  const moviePosterPath =
    formatTitleMoviePosterPath(props.catalogTitle) ?? formatTitleLikeVerticalImagePath(props.catalogTitle)
  const mobileMoviePosterPath = formatNonTitleLikeLandscapeImagePath(
    props.catalogTitle,
    ['16:9', '4:3', '3:2', '2:1'],
    ['title_art', 'angel_key_art_1', 'angel_key_art_2', 'angel_key_art_3', 'non_title_art'],
  )
  const rating = formatTitleRating(props.catalogTitle)
  const releaseYear = formatTitleReleaseYear(props.catalogTitle)
  const duration = formatTitleDuration(props.catalogTitle)
  const releaseDate = formatReleaseDate(locale, props.catalogTitle, props.filmDetails)

  return (
    <div className="grid grid-cols-1 gap-[32px] text-left lg:grid-cols-2">
      {moviePosterPath && (
        <div className="hidden lg:block">
          <img
            src={getCloudinaryImageUrl({ path: moviePosterPath, width: 638, height: 930 })}
            className="ml-auto"
            alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: props.catalogTitle.title })}
            height={930}
            width={638}
          />
        </div>
      )}
      {mobileMoviePosterPath && (
        <div className="block lg:hidden">
          <img
            src={getCloudinaryImageUrl({ path: mobileMoviePosterPath, width: 930, height: 523 })}
            className="ml-auto"
            alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: props.catalogTitle.title })}
            height={523}
            width={930}
          />
        </div>
      )}
      <div key="details" className="px-4 py-12 md:px-8 lg:max-w-[700px] se:px-2">
        {props.catalogTitle && (
          <>
            <HeadingMD weight="bold" color="white" as={AsH3} className="lg:photon-heading-lg mb-4">
              {props.catalogTitle.title}
            </HeadingMD>
            <div className="mb-4 flex items-center gap-2 lg:mb-6 lg:gap-4">
              <GuildRecommendedAge projectSlug={props.slug} iconSize={23} compact />
              {rating && (
                <div className="w-fit whitespace-nowrap rounded-full border-[1px] border-white px-4 py-1">
                  <EyebrowMD color="white" weight="bold">
                    {formatTitleRating(props.catalogTitle)}
                  </EyebrowMD>
                </div>
              )}
              {releaseYear && (
                <div className="w-fit whitespace-nowrap rounded-full border-[1px] border-white px-4 py-1">
                  <EyebrowMD color="white" weight="bold">
                    {releaseYear}
                  </EyebrowMD>
                </div>
              )}
              {duration && (
                <div className="w-fit whitespace-nowrap rounded-full border-[1px] border-white px-4 py-1">
                  <EyebrowMD color="white" weight="bold" className="lowercase">
                    {duration}
                  </EyebrowMD>
                </div>
              )}
              {props.guildScore ? <GuildScore guildScore={props.guildScore} /> : null}
            </div>
            <div className="mb-10">
              <DescriptorSection
                title={
                  isSeries(props.catalogTitle)
                    ? t('seriesSynopsis', 'Series Synopsis')
                    : t('filmSynopsis', 'Film synopsis')
                }
                description={
                  props.catalogTitle.description.long ??
                  props.catalogTitle.description.medium ??
                  props.catalogTitle.description.short
                }
              />
            </div>
          </>
        )}
        <div className="mb-8 grid grid-cols-1 gap-[32px] lg:grid-cols-2">
          {props.catalogTitle && releaseDate && (
            <DescriptorSection title={t('releaseDate', 'Release Date')} description={releaseDate} />
          )}
          <CrewSections catalogTitle={props.catalogTitle} />
        </div>
        <div>
          <CastRail catalogTitle={props.catalogTitle} />
        </div>
      </div>
    </div>
  )
}

export function formatReleaseDate(
  locale: string,
  catalogTitle: CatalogTitle,
  filmDetails: FilmDetailsType,
): string | undefined {
  if (filmDetails?.theatricalReleaseDateOverrideText) return filmDetails.theatricalReleaseDateOverrideText
  return formatTitleFriendlyReleaseDate(locale, catalogTitle)
}

export function CrewSections(props: { catalogTitle?: CatalogTitle }) {
  const { t } = useTranslate('theatrical-presales')
  if (!props.catalogTitle) return null

  const crew = formatTitleCrewMap(props.catalogTitle)

  const sections = [
    {
      title: t('filmDirectorV2', {
        count: crew['Director'].length,
        defaultValue: 'Director',
        defaultValue_other: 'Directors',
      }),
      names: crew['Director'],
    },
    {
      title: t('executiveProducerV2', {
        count: crew['Executive Producer'].length,
        defaultValue: 'Executive Producer',
        defaultValue_other: 'Executive Producers',
      }),
      names: crew['Executive Producer'],
    },
    {
      title: t('producerV2', {
        count: crew['Producer'].length,
        defaultValue: 'Producer',
        defaultValue_other: 'Producers',
      }),
      names: crew['Producer'],
    },
    {
      title: t('writer', {
        count: crew['Writer'].length,
        defaultValue: 'Writer',
        defaultValue_other: 'Writers',
      }),
      names: crew['Writer'],
    },
    {
      title: t('directorOfPhotography', {
        count: crew['Director of Photography'].length,
        defaultValue: 'Director of Photography',
        defaultValue_other: 'Directors of Photography',
      }),
      names: crew['Director of Photography'],
    },
    {
      title: t('artDirector', {
        count: crew['Art Director'].length,
        defaultValue: 'Art Director',
        defaultValue_other: 'Art Directors',
      }),
      names: crew['Art Director'],
    },
    {
      title: t('distributor', {
        count: crew['Distributor'].length,
        defaultValue: 'Distributor',
        defaultValue_other: 'Distributors',
      }),
      names: crew['Distributor'],
    },
    {
      title: t('cinematographer', {
        count: crew['Cinematographer'].length,
        defaultValue: 'Cinematographer',
        defaultValue_other: 'Cinematographers',
      }),
      names: crew['Cinematographer'],
    },
  ]

  return sections.map((section) => <CrewCredits key={section.title} names={section.names} title={section.title} />)
}
